/**
 * Navigation config — single source of truth for sidebar sections,
 * items, role visibility, and badge bindings.
 */

import type { ComponentType } from 'react';
import {
  DashboardIcon,
  InboxIcon,
  PhoneIcon,
  BerichtenIcon,
  NieuweAanvraagIcon,
  KlantenIcon,
  OpportunitiesIcon,
  OfferteIcon,
  OrderIcon,
  PackageIcon,
  FacturenIcon,
  CalendarIcon,
  WasserijIcon,
  ReparatieIcon,
  RisicoIcon,
  KlachtIcon,
  WebshopIcon,
  RoosterIcon,
  VerlofIcon,
  ClockIcon,
  PersoneelIcon,
  KasIcon,
  PrijzenIcon,
  CijfersIcon,
  FinancieelIcon,
  PartnersIcon,
  ControleIcon,
  CampagnesIcon,
  KennisbankIcon,
  AdsIcon,
  UsersIcon,
  SettingsIcon,
  GebiedenIcon,
  WerkbonTakenIcon,
  AbonnementIcon,
} from './sidebar-icons';

/**
 * `callcenter` is het extern loket (BizziPhone-antwoordservice, één gedeeld
 * account): alleen Nieuwe aanvraag, Prijzen en Kennisbank. Bron van die grens
 * is src/lib/extern-loket.ts; de API-kant zit in api/_lib/extern-loket.ts.
 */
export type Role = 'admin' | 'medewerker' | 'callcenter';

export type BadgeKey =
  | 'inbox-new'
  | 'berichten-unread'
  | 'webshop-pending'
  | 'kas-achterstand'
  | 'risicos-open'
  | 'verlof-pending'
  | 'declaraties-open'
  | 'klachten-open'
  | 'reparaties-toplannen'
  | 'ophaal-actie-vereist'
  | 'opvolgen-open'
  | 'signalen-open'
  | 'social-wachtrij'
  | 'route-voorstellen';

export type NavItem = {
  id: string;
  name: string;
  href: string;
  icon: ComponentType;
  roles: Role[];
  badge?: BadgeKey;
};

export type NavSection = {
  id: string;
  label: string | null;
  collapsible: boolean;
  defaultOpen: boolean;
  items: NavItem[];
};

export const NAVIGATION: NavSection[] = [
  {
    id: 'werkruimte',
    label: null,
    collapsible: false,
    defaultOpen: true,
    items: [
      { id: 'dashboard', name: 'Dashboard', href: '/', icon: DashboardIcon, roles: ['admin', 'medewerker'] },
    ],
  },
  {
    id: 'office',
    label: 'Office',
    collapsible: true,
    defaultOpen: true,
    items: [
      { id: 'klanten', name: 'Klanten', href: '/klanten', icon: KlantenIcon, roles: ['admin', 'medewerker'] },
      // Lopende service-abonnementen over alle klanten heen. Read-only venster:
      // muteren blijft op de klantpagina (AbonnementenSection), zodat er maar
      // één plek is die schrijft.
      { id: 'abonnementen', name: 'Abonnementen', href: '/abonnementen', icon: AbonnementIcon, roles: ['admin', 'medewerker'] },
      { id: 'offertes', name: 'Offertes', href: '/offertes', icon: OfferteIcon, roles: ['admin', 'medewerker'] },
      { id: 'facturen', name: 'Facturen', href: '/facturen', icon: FacturenIcon, roles: ['admin', 'medewerker'] },
      { id: 'inbox', name: 'Inbox', href: '/inbox', icon: InboxIcon, roles: ['admin', 'medewerker'], badge: 'inbox-new' },
      { id: 'telefoon', name: 'Telefoon', href: '/telefoon', icon: PhoneIcon, roles: ['admin', 'medewerker'], badge: 'opvolgen-open' },
      { id: 'berichten', name: 'Berichten', href: '/berichten', icon: BerichtenIcon, roles: ['admin', 'medewerker'], badge: 'berichten-unread' },
      // Systeemsignalen (convert, doorverwijs, financieel): kantoorwerk.
      { id: 'signalen', name: 'Signalen', href: '/signalen', icon: WerkbonTakenIcon, roles: ['admin', 'medewerker'], badge: 'signalen-open' },
      { id: 'nieuwe-aanvraag', name: 'Nieuwe aanvraag', href: '/nieuwe-aanvraag', icon: NieuweAanvraagIcon, roles: ['admin', 'medewerker', 'callcenter'] },
      { id: 'prijzen', name: 'Prijzen', href: '/prijzen', icon: PrijzenIcon, roles: ['admin', 'medewerker', 'callcenter'] },
      { id: 'kennis', name: 'Kennisbank', href: '/kennis', icon: KennisbankIcon, roles: ['admin', 'medewerker', 'callcenter'] },
      { id: 'webshop', name: 'Webshop', href: '/webshop', icon: WebshopIcon, roles: ['admin', 'medewerker'], badge: 'webshop-pending' },
      { id: 'kas', name: 'Kas', href: '/kas', icon: KasIcon, roles: ['admin', 'medewerker'], badge: 'kas-achterstand' },
      { id: 'inkoop', name: 'Inkoop', href: '/inkoop', icon: PackageIcon, roles: ['admin', 'medewerker'] },
    ],
  },
  {
    id: 'planning',
    label: 'Planning',
    collapsible: true,
    defaultOpen: true,
    items: [
      { id: 'routes', name: 'Routes', href: '/kalender', icon: CalendarIcon, roles: ['admin', 'medewerker'], badge: 'route-voorstellen' },
      { id: 'rooster', name: 'Rooster', href: '/rooster', icon: RoosterIcon, roles: ['admin', 'medewerker'] },
    ],
  },
  {
    id: 'operatie',
    label: 'Uitvoering',
    collapsible: true,
    defaultOpen: true,
    items: [
      { id: 'orders', name: 'Orders', href: '/orders', icon: OrderIcon, roles: ['admin', 'medewerker'], badge: 'ophaal-actie-vereist' },
      { id: 'items', name: 'Items', href: '/items', icon: PackageIcon, roles: ['admin', 'medewerker'] },
      { id: 'wasserij', name: 'Wasserij', href: '/wasserij', icon: WasserijIcon, roles: ['admin', 'medewerker'] },
      { id: 'reparaties', name: 'Reparaties', href: '/reparaties', icon: ReparatieIcon, roles: ['admin', 'medewerker'], badge: 'reparaties-toplannen' },
      { id: 'reparateurs', name: 'Reparateurs', href: '/reparateurs', icon: PartnersIcon, roles: ['admin', 'medewerker'] },
      { id: 'risicos', name: "Risico's", href: '/risicos', icon: RisicoIcon, roles: ['admin', 'medewerker'], badge: 'risicos-open' },
      { id: 'klachten', name: 'Klachten', href: '/klachten', icon: KlachtIcon, roles: ['admin', 'medewerker'], badge: 'klachten-open' },
    ],
  },
  {
    id: 'sales',
    label: 'Sales',
    collapsible: true,
    defaultOpen: true,
    items: [
      { id: 'opportunities', name: 'Opportunities', href: '/opportunities', icon: OpportunitiesIcon, roles: ['admin'] },
    ],
  },
  {
    id: 'team',
    label: 'Team',
    collapsible: true,
    defaultOpen: false,
    items: [
      { id: 'personeel', name: 'Personeel', href: '/personeel', icon: PersoneelIcon, roles: ['admin'] },
      { id: 'verlof', name: 'Verlof', href: '/verlof', icon: VerlofIcon, roles: ['admin', 'medewerker'], badge: 'verlof-pending' },
      { id: 'declaraties', name: 'Declaraties', href: '/declaraties', icon: KasIcon, roles: ['admin', 'medewerker'], badge: 'declaraties-open' },
      { id: 'uren', name: 'Uren', href: '/uren', icon: ClockIcon, roles: ['admin'] },
    ],
  },
  {
    id: 'financieel',
    label: 'Financieel',
    collapsible: true,
    defaultOpen: false,
    items: [
      { id: 'cijfers', name: 'Cijfers', href: '/cijfers', icon: CijfersIcon, roles: ['admin'] },
      { id: 'financieel', name: 'Financieel', href: '/financieel', icon: FinancieelIcon, roles: ['admin'] },
      { id: 'partners', name: 'Partners', href: '/partners', icon: PartnersIcon, roles: ['admin'] },
      { id: 'platform', name: 'Cinco Platform', href: '/platform', icon: PartnersIcon, roles: ['admin'] },
      { id: 'controle', name: 'Controle', href: '/controle', icon: ControleIcon, roles: ['admin'] },
    ],
  },
  {
    id: 'marketing',
    label: 'Marketing',
    collapsible: true,
    defaultOpen: false,
    items: [
      { id: 'marketing', name: 'E-mailmarketing', href: '/marketing', icon: CampagnesIcon, roles: ['admin'] },
      { id: 'seo-content', name: 'SEO & Content', href: '/marketing/seo-content', icon: CampagnesIcon, roles: ['admin'] },
      // Social staat open voor kantoor (Ilse keurt en bewerkt sinds 16-10); de rest van Marketing blijft admin.
      { id: 'social', name: 'Social', href: '/marketing/social', icon: CampagnesIcon, roles: ['admin', 'medewerker'], badge: 'social-wachtrij' },
      { id: 'gbp', name: 'Google Bedrijfsprofiel', href: '/marketing/gbp', icon: CampagnesIcon, roles: ['admin'] },
      { id: 'beeldbibliotheek', name: 'Beeldbibliotheek', href: '/marketing/beeldbibliotheek', icon: CampagnesIcon, roles: ['admin'] },
      { id: 'ads', name: 'Ads', href: '/ads', icon: AdsIcon, roles: ['admin'] },
    ],
  },
  {
    id: 'beheer',
    label: 'Beheer',
    collapsible: true,
    defaultOpen: false,
    items: [
      // Wijzigingslogboek over alle kanalen (actor-attributie fase 3) — het
      // toezichtsvenster op wat mensen, klanten en agents in het platform doen.
      { id: 'logboek', name: 'Logboek', href: '/logboek', icon: ControleIcon, roles: ['admin', 'medewerker'] },
      // Wat Jasper (werkbank) en kantoor neerleggen om op te lossen; zelfde tabel als wacht.py en de briefing.
      { id: 'wachtlijst', name: 'Wachtlijst', href: '/wachtlijst', icon: ControleIcon, roles: ['admin', 'medewerker'] },
      { id: 'automatisering', name: 'Automatisering', href: '/automatisering', icon: SettingsIcon, roles: ['admin'] },
      { id: 'kennisbank', name: 'Kennisartikelen', href: '/kennisbank', icon: KennisbankIcon, roles: ['admin'] },
      { id: 'agent-verbeteringen', name: 'Agent-verbeteringen', href: '/agent-verbeteringen', icon: KennisbankIcon, roles: ['admin', 'medewerker'] },
      { id: 'telefoon-instellingen', name: 'Telefoon-instellingen', href: '/telefoon-instellingen', icon: PhoneIcon, roles: ['admin'] },
      { id: 'gebieden', name: 'Gebieden & routes', href: '/gebieden', icon: GebiedenIcon, roles: ['admin'] },
      { id: 'gebruikers', name: 'Gebruikers', href: '/gebruikers', icon: UsersIcon, roles: ['admin'] },
      { id: 'instellingen', name: 'Instellingen', href: '/instellingen', icon: SettingsIcon, roles: ['admin', 'medewerker'] },
    ],
  },
];

export function filterNavigationByRole(role: Role): NavSection[] {
  return NAVIGATION
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => item.roles.includes(role)),
    }))
    .filter((section) => section.items.length > 0);
}
